import type { QuestionRecord } from "@/components/courses/QuestionEditor";
import StudentBodyParts from "@/components/student/StudentBodyParts";
import StudentKnowledgeBanner from "@/components/student/StudentKnowledgeBanner";
import StudentListen from "@/components/student/StudentListen";
import StudentSlideDecks from "@/components/student/StudentSlideDecks";
import StudentKeyTerms from "@/components/student/StudentKeyTerms";
import {
  type ActivityIncludes,
  type BodyPart,
  partHasContent,
} from "@/lib/activityAnatomy";
import {
  FILL_BLANK_TOKEN,
  normalizeQuestionOptions,
  promptToHtml,
  undecorateFillBlanks,
  type ChoiceOption,
  type MatchingPair,
} from "@/lib/questionScoring";
import { normalizeSlideDecks, type OutlineTarget, type SlideDeck, type SlideMode } from "@/lib/slideDecks";
import { ITEM_ICON, ITEM_LABEL } from "@/pages/student/itemTypes";
import { activitySpeakText } from "@/lib/studentSpeak";
import { ClipboardCheck, Plus } from "lucide-react";
import StudentMatchingBoard from "./StudentMatchingBoard";

type KeyTerm = { id: string; term: string; definition: string };

export default function StudentActivityPreview({
  type,
  title,
  estimatedMinutes,
  dueAt,
  timeLimitMinutes,
  maxAttempts,
  includes,
  parts,
  questions = [],
  questionsLoading = false,
  hasQuestions = false,
  requireKnowledge = true,
  terms = [],
  decks = [],
  slideMode = null,
  outlineTargets = [],
  assignmentPoints = null,
  statusLabel = null,
}: {
  type: string;
  title: string;
  estimatedMinutes: number;
  dueAt: string;
  timeLimitMinutes: number;
  maxAttempts: number | "";
  includes: ActivityIncludes;
  parts: BodyPart[];
  questions?: QuestionRecord[];
  questionsLoading?: boolean;
  hasQuestions?: boolean;
  requireKnowledge?: boolean;
  terms?: KeyTerm[];
  decks?: SlideDeck[];
  slideMode?: SlideMode | null;
  outlineTargets?: OutlineTarget[];
  assignmentPoints?: number | null;
  statusLabel?: string | null;
}) {
  const Icon = ITEM_ICON[type] || ClipboardCheck;
  const typeLabel = ITEM_LABEL[type] || type;
  const contentParts = parts.filter(partHasContent);
  const filledTerms = terms.filter((row) => row.term.trim() || row.definition.trim());
  const filledDecks = normalizeSlideDecks(decks);
  const questionPts = questions.reduce((sum, q) => sum + (Number(q.points) || 0), 0);
  const dueLabel = formatDue(dueAt);
  const attemptLabel =
    maxAttempts === "" ? "Unlimited attempts" : `${maxAttempts} attempt${Number(maxAttempts) === 1 ? "" : "s"}`;

  return (
    <div className="student-page">
      <div className="student-page-head">
        <span className="student-eyebrow">
          <Icon size={12} aria-hidden /> {typeLabel}
        </span>
        <h1>{title.trim() || "Untitled"}</h1>
        <StudentListen
          text={activitySpeakText({
            title,
            parts: contentParts,
            terms: filledTerms,
          })}
        />
        <div className="student-chips">
          {statusLabel ? <span className="student-chip">{statusLabel}</span> : null}
          {estimatedMinutes > 0 ? <span className="student-chip">{estimatedMinutes} min</span> : null}
          {includes.timeLimitMinutes && timeLimitMinutes > 0 ? (
            <span className="student-chip">{timeLimitMinutes} min limit</span>
          ) : null}
          {includes.dueAt && dueLabel ? <span className="student-chip">Due {dueLabel}</span> : null}
          {includes.maxAttempts ? <span className="student-chip">{attemptLabel}</span> : null}
          {hasQuestions && questionPts > 0 ? <span className="student-chip">{questionPts} points</span> : null}
          {!hasQuestions && assignmentPoints ? (
            <span className="student-chip">{assignmentPoints} points</span>
          ) : null}
        </div>
      </div>

      <StudentKnowledgeBanner show={hasQuestions && requireKnowledge !== false} />

      {contentParts.length ? (
        <div className="student-reading">
          <StudentBodyParts parts={contentParts} />
        </div>
      ) : null}

      {filledDecks.length ? (
        <StudentSlideDecks
          decks={filledDecks}
          targets={outlineTargets}
          mode={slideMode}
          heading={type !== "slides"}
        />
      ) : null}

      {filledTerms.length ? (
        <StudentKeyTerms
          terms={filledTerms.map((row) => ({ term: row.term, definition: row.definition }))}
          heading={type !== "key_terms"}
        />
      ) : null}

      {!contentParts.length && !filledDecks.length && !filledTerms.length && !hasQuestions ? (
        <p className="student-muted">Nothing to read here yet.</p>
      ) : null}

      {hasQuestions ? (
        <div className="student-section">
          <div className="student-section__head">
            <h2>Questions</h2>
          </div>
          {questionsLoading ? (
            <p className="student-muted">Loading questions…</p>
          ) : questions.length === 0 ? (
            <p className="student-muted">No questions yet.</p>
          ) : (
            <div className="student-questions">
              {questions.map((question, index) => (
                <StudentQuestionPreview key={question.id} question={question} index={index} />
              ))}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

function StudentQuestionPreview({ question, index }: { question: QuestionRecord; index: number }) {
  const options = normalizeQuestionOptions(question.options, question.type, question.presentation);
  const choices = options.filter((row): row is ChoiceOption => "label" in row);
  const pairs = options.filter((row): row is MatchingPair => "left" in row);
  const fillBlanks = question.type === "fill_blank";
  const promptHtml = studentPromptHtml(question.prompt, fillBlanks);
  const presentation = question.presentation;
  const dropdown = question.type === "single_choice" && presentation === "dropdown";
  const multi = question.type === "multiple_choice";
  const pts = Number(question.points) || 0;

  return (
    <div className="student-question">
      <div className="student-question__head">
        <span className="student-question__n">
          <span className="student-vh">Question </span>
          {index + 1}
        </span>
        {question.type !== "directions" && pts ? (
          <p className="student-question__meta">
            {pts} {pts === 1 ? "point" : "points"}
          </p>
        ) : null}
      </div>
      <div className="student-question__prompt" dangerouslySetInnerHTML={{ __html: promptHtml }} />
      {question.type === "directions" || fillBlanks ? null : question.type === "matching" ? (
        <StudentMatchingBoard
          lefts={pairs.map((pair) => ({ id: pair.id, text: pair.left }))}
          rights={pairs.map((pair, index) => ({ id: 100000 + index + 1, text: pair.right }))}
          disabled
          seed={question.id}
        />
      ) : question.type === "long_answer" ? (
        <textarea className="student-textarea" disabled placeholder="Your answer" rows={4} />
      ) : question.type === "short_answer" ? (
        <input className="student-input" disabled placeholder="Your answer" />
      ) : question.type === "number" ? (
        <input className="student-input" type="number" disabled placeholder="0" />
      ) : question.type === "date" ? (
        <input className="student-input" type="date" disabled />
      ) : question.type === "upload" ? (
        <div className="student-uploads">
          <button type="button" className="student-upload-tile student-upload-tile--ghost" disabled>
            <Plus size={22} aria-hidden />
            <span>
              {question.allowMultiple === false || question.maxFiles === 1 ? "Attach file" : "Attach files"}
            </span>
          </button>
        </div>
      ) : dropdown ? (
        <select className="student-input" disabled defaultValue="">
          <option value="">Choose…</option>
          {choices.map((choice) => (
            <option key={choice.id} value={choice.id}>
              {choice.label.trim() || "Option"}
            </option>
          ))}
        </select>
      ) : choices.length ? (
        <div className="student-choices">
          {choices.map((choice) => (
            <button key={choice.id} type="button" className="student-choice" disabled>
              <span>{choice.label.trim() || "Option"}</span>
              <span aria-hidden>{multi ? "☐" : "○"}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function studentPromptHtml(value: string, fillBlanks: boolean) {
  const html = promptToHtml(value) || "<p>(No prompt)</p>";
  if (!fillBlanks) return html;
  return undecorateFillBlanks(html).replace(
    new RegExp(FILL_BLANK_TOKEN.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g"),
    '<input class="student-blank" disabled aria-label="Blank" />',
  );
}

function formatDue(value: string) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

